import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { mcpWrite } from '../../lib/mcp';
import { useMcpTools } from '../../lib/useMcpTools';
import { useToast } from '../Toast/ToastProvider';
import './DeleteRowButton.css';

/**
 * Per-row Delete — backlog 1033, ruled by Mike 2026-09-10: rebuild it, and refuse to delete
 * anything with history.
 *
 * THIS IS FOR REMOVING A DUPLICATE, NOT FOR REMOVING SOMEONE WHO HAS TRADED. The refusal
 * lives in the MCP tool, not here: it reads every foreign key pointing at the row from
 * `pg_constraint` at call time and refuses if any referencing row exists, naming each table
 * and its count. **A guard in this component would be a suggestion** — the console is not the
 * only caller of those tools, and a UI check is bypassed by anything that is not this button.
 *
 * So what this does is: ask twice, call, and put the tool's own refusal in front of the
 * operator verbatim. The message it shows on a refusal is the useful one — "coop_balances: 1
 * [would CASCADE]" tells an operator exactly what they were about to destroy.
 *
 * THE BUTTON HIDES ITSELF unless the operator's role actually holds the grant. `can()` reads
 * the tool list the MCP returns for their role, which is derived from `role_tools` — so this
 * is the same authority the server enforces with, not a parallel guess at it. Owner and Admin
 * hold these; Support deliberately does not, though Support CAN deactivate a dealer.
 */
export function DeleteRowButton<T extends { id: number }>(props: {
  row: T;
  /** camelCase MCP action, e.g. `deleteDealer`. */
  action: string;
  /** What the operator is deleting, for the confirmation copy: "dealer", "rep". */
  noun: string;
  /** Human label for this row — a company name, a person's name. */
  label: string;
  /** Query key to invalidate so the table reflects the removal. */
  invalidate: unknown[];
}) {
  const { can } = useMcpTools();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!can(props.action)) return null;

  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      await mcpWrite(props.action, { id: props.row.id });
      notify({ variant: 'success', message: `Deleted ${props.noun} “${props.label}”.` });
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: props.invalidate });
    } catch (err) {
      // The tool's refusal names every table holding a reference and how many rows. Show it
      // whole rather than summarising — "has dependent records" without the list sends the
      // operator hunting, and the list is the entire value of the check.
      notify({ variant: 'error', message: (err as Error).message });
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        className="rowdelete__trigger"
        onClick={() => setConfirming(true)}
        aria-label={`Delete ${props.noun} ${props.label}`}
      >
        Delete
      </button>
    );
  }

  return (
    <span className="rowdelete__confirm">
      {/* Deliberately not a window.confirm: it cannot be styled, it cannot say WHICH row, and
          it is the one dialog people dismiss without reading. */}
      <span className="rowdelete__ask">Delete permanently?</span>
      <button type="button" className="rowdelete__yes" disabled={busy} onClick={remove}>
        {busy ? 'Deleting…' : 'Yes, delete'}
      </button>
      <button type="button" className="rowdelete__no" disabled={busy} onClick={() => setConfirming(false)}>
        Cancel
      </button>
    </span>
  );
}
